import {Node, mergeAttributes} from '@tiptap/core'

export const DEFAULT_FONT_SIZE = 23
export const DEFAULT_BUTTON_COLOR = '#000000'
export const DEFAULT_BUTTON_BACKGROUND_COLOR = '#ffffff'
export const DEFAULT_BUTTON_ALIGNMENT = 'unset'
export const DEFAULT_BUTTON_TEXT = 'New Button'

export type CustomButtonAttrs = {
  text: string
  href: string
  fontSize: number
  color: string
  backgroundColor: string
  alignment: string
  width: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customButton: {
      insertCustomButton: (attrs?: Partial<CustomButtonAttrs>) => ReturnType
      updateCustomButton: (attrs: Partial<CustomButtonAttrs>) => ReturnType
    }
  }
}

const CustomButton = Node.create({
  name: 'customButton',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      text: {default: DEFAULT_BUTTON_TEXT},
      href: {default: ''},
      fontSize: {default: DEFAULT_FONT_SIZE},
      color: {default: DEFAULT_BUTTON_COLOR},
      backgroundColor: {default: DEFAULT_BUTTON_BACKGROUND_COLOR},
      alignment: {default: DEFAULT_BUTTON_ALIGNMENT},
      width: {default: 'auto'},
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-custom-button]',
        getAttrs: (node) => {
          const element = node as HTMLElement
          const anchor = element.querySelector('a')
          if (!anchor) {
            return false
          }

          const style = anchor.getAttribute('style') || ''
          const fontSize = style.match(/font-size:\s*(\d+)px/)
          const color = style.match(
            /(?:^|;)\s*color:\s*(#[0-9a-fA-F]{3,8}|[a-z]+)/,
          )
          const backgroundColor = style.match(
            /background-color:\s*(#[0-9a-fA-F]{3,8}|[a-z]+)/,
          )
          const wrapperStyle = element.getAttribute('style') || ''
          const textAlign = wrapperStyle.match(/text-align:\s*(\w+)/)

          const anchorStyle = anchor.getAttribute('style') || ''
          const widthMatch = anchorStyle.match(/width:\s*([^;]+)/)

          return {
            text: anchor.textContent || DEFAULT_BUTTON_TEXT,
            href: anchor.getAttribute('href') || '',
            fontSize: fontSize ? parseInt(fontSize[1], 10) : DEFAULT_FONT_SIZE,
            color: color ? color[1] : DEFAULT_BUTTON_COLOR,
            backgroundColor: backgroundColor
              ? backgroundColor[1]
              : DEFAULT_BUTTON_BACKGROUND_COLOR,
            alignment: textAlign ? textAlign[1] : DEFAULT_BUTTON_ALIGNMENT,
            width: widthMatch ? widthMatch[1].trim() : 'auto',
          }
        },
      },
    ]
  },

  renderHTML({HTMLAttributes}) {
    const {
      text,
      href,
      fontSize,
      color,
      backgroundColor,
      alignment,
      width,
    } = HTMLAttributes

    const wrapperStyle =
      alignment && alignment !== 'unset'
        ? `text-align: ${alignment}; margin: 10px 0;`
        : 'text-align: center; margin: 10px 0;'

    const widthStyle = width && width !== 'auto' ? `width: ${width};` : ''

    return [
      'div',
      mergeAttributes({'data-custom-button': ''}, {style: wrapperStyle}),
      [
        'a',
        {
          href: href || '#',
          style: `display: inline-block; padding: 12px 24px; font-size: ${fontSize}px; color: ${color}; background-color: ${backgroundColor}; text-decoration: none; border-radius: 4px;${widthStyle}`,
        },
        text || DEFAULT_BUTTON_TEXT,
      ],
    ]
  },

  addNodeView() {
    return ({node, getPos, editor}) => {
      const dom = document.createElement('div')
      dom.setAttribute('data-custom-button', '')
      dom.classList.add('html-editor-custom-button-wrapper')

      const updateDom = (attrs: CustomButtonAttrs) => {
        const alignment =
          attrs.alignment && attrs.alignment !== 'unset'
            ? attrs.alignment
            : 'center'
        dom.style.textAlign = alignment
        dom.style.margin = '10px 0'

        const widthStyle =
          attrs.width && attrs.width !== 'auto' ? `width: ${attrs.width};` : ''

        dom.innerHTML = `<a href="${
          attrs.href || '#'
        }" style="display: inline-block; padding: 12px 24px; font-size: ${
          attrs.fontSize
        }px; color: ${attrs.color}; background-color: ${
          attrs.backgroundColor
        }; text-decoration: none; border-radius: 4px; pointer-events: none;${widthStyle}">${
          attrs.text || DEFAULT_BUTTON_TEXT
        }</a>`
      }

      updateDom(node.attrs as CustomButtonAttrs)

      dom.addEventListener('click', () => {
        if (!editor.isEditable || typeof getPos !== 'function') {
          return
        }

        const pos = getPos()
        editor.chain().focus().setNodeSelection(pos).run()
      })

      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'customButton') {
            return false
          }
          updateDom(updatedNode.attrs as CustomButtonAttrs)
          return true
        },
      }
    }
  },

  addCommands() {
    return {
      insertCustomButton: (attrs?: Partial<CustomButtonAttrs>) => ({
        commands,
      }) => {
        return commands.insertContent({
          type: 'customButton',
          attrs: {
            text: DEFAULT_BUTTON_TEXT,
            href: '',
            fontSize: DEFAULT_FONT_SIZE,
            color: DEFAULT_BUTTON_COLOR,
            backgroundColor: DEFAULT_BUTTON_BACKGROUND_COLOR,
            alignment: DEFAULT_BUTTON_ALIGNMENT,
            width: 'auto',
            ...attrs,
          },
        })
      },

      updateCustomButton: (attrs: Partial<CustomButtonAttrs>) => ({
        tr,
        state,
        dispatch,
      }) => {
        const {selection} = state
        const node = state.doc.nodeAt(selection.from)
        if (!node || node.type.name !== 'customButton') {
          return false
        }

        if (dispatch) {
          tr.setNodeMarkup(selection.from, undefined, {
            ...node.attrs,
            ...attrs,
          })
          dispatch(tr)
        }

        return true
      },
    }
  },
})

export default CustomButton
